import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "@/lib/api";
import type { TagKey, TagValue } from "@/lib/types";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, Input } from "@/components/ui/field";
import { ErrorState, Loading } from "@/components/query-state";
import { useHasPermission } from "@/auth/usePermission";

const MAX_VALUES = 50;
const emptyValue = (): TagValue => ({ value: "", label: "" });

function blockingPolicies(error: unknown): string[] {
  if (!(error instanceof ApiError) || error.status !== 409) return [];
  const body = error.body;
  if (!body || typeof body !== "object" || !("policyIds" in body) || !Array.isArray(body.policyIds)) return [];
  return body.policyIds.filter((id): id is string => typeof id === "string");
}

function TagKeyForm({ editing, onDone }: { editing: TagKey | null; onDone: () => void }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [key, setKey] = useState(editing?.key ?? "");
  const [label, setLabel] = useState(editing?.label ?? "");
  const [values, setValues] = useState<TagValue[]>(editing?.values.length ? editing.values : [emptyValue()]);
  const save = useMutation({
    mutationFn: () => api.putTagKey({
      key: key.trim(),
      label: label.trim(),
      values: values.map((item) => ({ value: item.value.trim(), label: item.label.trim() })),
    }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["tag-keys"] });
      if (!editing) { setKey(""); setLabel(""); setValues([emptyValue()]); }
    },
  });
  const complete = key.trim() && label.trim() && values.length > 0 && values.every((item) => item.value.trim() && item.label.trim());
  const setValue = (index: number, patch: Partial<TagValue>) =>
    setValues((current) => current.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  const submit = (event: FormEvent) => { event.preventDefault(); save.mutate(); };

  return (
    <form className="flex flex-col gap-3" onSubmit={submit}>
      <Field label={t("tagKeys.key")} htmlFor="tag-key" hint={t("tagKeys.keyHint")}>
        <Input id="tag-key" maxLength={40} value={key} disabled={Boolean(editing)} onChange={(event) => setKey(event.target.value)} />
      </Field>
      <Field label={t("tagKeys.label")} htmlFor="tag-label">
        <Input id="tag-label" maxLength={200} value={label} onChange={(event) => setLabel(event.target.value)} />
      </Field>
      <fieldset className="flex min-w-0 flex-col gap-3">
        <legend className="text-secondary mb-2 text-sm font-medium">{t("tagKeys.values")}</legend>
        {values.map((item, index) => (
          <div key={index} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_2rem] items-end gap-2">
            <Field label={t("tagKeys.value", { index: index + 1 })} htmlFor={`tag-value-${index}`}>
              <Input id={`tag-value-${index}`} maxLength={40} value={item.value} onChange={(event) => setValue(index, { value: event.target.value })} />
            </Field>
            <Field label={t("tagKeys.valueLabel", { index: index + 1 })} htmlFor={`tag-value-label-${index}`}>
              <Input id={`tag-value-label-${index}`} maxLength={200} value={item.label} onChange={(event) => setValue(index, { label: event.target.value })} />
            </Field>
            <Button
              className="h-10 w-8 px-0"
              variant="ghost"
              size="sm"
              disabled={values.length <= 1}
              aria-label={t("tagKeys.removeValue", { index: index + 1 })}
              onClick={() => setValues((current) => current.filter((_, i) => i !== index))}
            >
              <Trash2 size={14} aria-hidden />
            </Button>
          </div>
        ))}
        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" size="sm" disabled={values.length >= MAX_VALUES} onClick={() => setValues((current) => [...current, emptyValue()])}>
            <Plus size={14} aria-hidden /> {t("tagKeys.addValue")}
          </Button>
          <p className="text-muted text-xs">{t("tagKeys.valueCount", { count: values.length, max: MAX_VALUES })}</p>
        </div>
      </fieldset>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" disabled={!complete || save.isPending}>{t("tagKeys.save")}</Button>
        {editing ? <Button variant="secondary" onClick={onDone}>{t("common.cancel")}</Button> : null}
        {save.isSuccess ? <span role="status" className="text-sm text-[#2f6b21]">{t("tagKeys.saved")}</span> : null}
      </div>
      {save.isError ? <p role="alert" className="text-danger text-sm">{save.error.message}</p> : null}
    </form>
  );
}

function TagKeyCard({ tagKey, onEdit }: { tagKey: TagKey; onEdit: () => void }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const remove = useMutation({
    mutationFn: () => api.deleteTagKey(tagKey.key),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ["tag-keys"] }); },
    onSettled: () => setConfirming(false),
  });
  const blocking = blockingPolicies(remove.error);
  return (
    <Card>
      <CardBody className="flex flex-col gap-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1 break-words">
            <p className="text-foreground text-sm font-medium">{tagKey.label}</p>
            <p className="text-muted text-xs">{tagKey.key}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" aria-label={t("tagKeys.editAria", { label: tagKey.label })} onClick={onEdit}>{t("tagKeys.edit")}</Button>
            {confirming ? (
              <Button variant="danger" size="sm" disabled={remove.isPending} onClick={() => remove.mutate()}>
                {t("tagKeys.confirmDelete", { label: tagKey.label })}
              </Button>
            ) : (
              <Button variant="ghost" size="sm" aria-label={t("tagKeys.deleteAria", { label: tagKey.label })} onClick={() => setConfirming(true)}>
                <Trash2 size={14} aria-hidden /> {t("tagKeys.delete")}
              </Button>
            )}
          </div>
        </div>
        <ul className="flex flex-wrap gap-2">
          {tagKey.values.map((item) => (
            <li key={item.value} className="bg-surface-sunken border-border-subtle inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs">
              <span className="text-foreground">{item.label}</span>
              <span className="text-muted">{item.value}</span>
            </li>
          ))}
        </ul>
        {remove.isError ? (
          <div role="alert" className="text-danger flex flex-col text-sm">
            <span>{remove.error.message}</span>
            {blocking.length ? <span>{t("tagKeys.inUse", { ids: blocking.join(", ") })}</span> : null}
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

export function TagKeysPage() {
  const { t } = useTranslation();
  const isAdmin = useHasPermission("BIBLE_ADMIN");
  const tagKeys = useQuery({ queryKey: ["tag-keys"], queryFn: api.listTagKeys, enabled: isAdmin });
  const [editing, setEditing] = useState<TagKey | null>(null);

  const header = <PageHeader title={t("tagKeys.title")} description={t("tagKeys.description")} sacredIcon="seal" sacredCaption="holy.tagKeysCaption" />;
  if (!isAdmin) return <>{header}<ErrorState error={new ApiError(403, "Forbidden")} /></>;

  return (
    <>
      {header}
      {tagKeys.isPending ? <Loading /> : tagKeys.isError ? <ErrorState error={tagKeys.error} /> : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_24rem]">
          <div className="flex min-w-0 flex-col gap-3">
            {tagKeys.data.length === 0 ? <p className="text-muted text-sm">{t("tagKeys.empty")}</p> : null}
            {tagKeys.data.map((tagKey) => (
              <TagKeyCard key={tagKey.key} tagKey={tagKey} onEdit={() => setEditing(tagKey)} />
            ))}
          </div>
          <Card className="h-fit min-w-0">
            <CardHeader><CardTitle sacredIcon="seal">{editing ? t("tagKeys.editKey", { key: editing.key }) : t("tagKeys.newKey")}</CardTitle></CardHeader>
            <CardBody>
              <TagKeyForm key={editing?.key ?? "new"} editing={editing} onDone={() => setEditing(null)} />
            </CardBody>
          </Card>
        </div>
      )}
    </>
  );
}
